import { ActionReducerMapBuilder, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { EntityStoreName } from 'app/enums/store';
import { PaginatedResponse } from 'app/interfaces/api';
import { TransactionDto } from 'app/interfaces/dto';
import { apiRequestCall } from 'app/services/api';
import { ApiResource } from 'app/services/api/enums';
import { cancelationSubscribe } from 'app/services/api/utils';
import { store } from 'redux/store';
import { TransactionsHistoryState } from './interfaces';
import { Pagination } from 'app/interfaces';
import { entityStoreStatus } from 'app/utils/store/entity-store-status';
import { TransactionsHistoryStoreStatus } from './enums';
import { entityAdapter } from './adapter';

export const getTransactionsList = createAsyncThunk(
  `${EntityStoreName.Transactions}/getList`,
  (_, { signal }): Promise<PaginatedResponse<TransactionDto[]>> => {
    const cancelToken = cancelationSubscribe(signal);
    const { limit, offset, reload } = store.getState()[EntityStoreName.Transactions];

    return apiRequestCall<PaginatedResponse<TransactionDto[]>>({
      params: {
        method: 'GET',
        resource: ApiResource.Payments,
        urlPrefix: 'users/transactions',
        params: {
          limit,
          offset: reload ? 0 : offset,
        },
        cancelToken,
      },
    });
  }
);

export const updatePaginationReducer = (
  state: TransactionsHistoryState,
  action: PayloadAction<{ data: Partial<Pagination> }>
) => {
  const { limit, offset, reload } = action.payload.data;

  state.limit = limit ?? state.limit;
  state.offset = offset ?? state.offset;
  state.reload = reload ?? state.reload;
};

export const registerReducers = (
  builder: ActionReducerMapBuilder<TransactionsHistoryState>
): void => {
  builder
    .addCase(getTransactionsList.pending, (state, action) => {
      entityStoreStatus.addRequestId(
        state,
        TransactionsHistoryStoreStatus.Loading,
        action.meta.requestId
      );
    })
    .addCase(getTransactionsList.fulfilled, (state, action) => {
      if (state.reload) {
        entityAdapter.setAll(state, action.payload.data);
      } else {
        entityAdapter.addMany(state, action.payload.data);
      }

      state.total = action.payload.total;
      state.offset = state.reload
        ? action.payload.data.length
        : state.offset + action.payload.data.length;
      entityStoreStatus.removeRequestId(
        state,
        TransactionsHistoryStoreStatus.Loading,
        action.meta.requestId
      );
    })
    .addCase(getTransactionsList.rejected, (state, action) => {
      entityStoreStatus.removeRequestId(
        state,
        TransactionsHistoryStoreStatus.Loading,
        action.meta.requestId
      );
    });
};
